import {
  Box,
  GKModal,
  Grid2,
  InfoItalicIcon,
  List,
  ListItem,
  RightIcon,
  Typography,
} from "components";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SETTING_TAB } from "store/slices/appSlice";
import { useAppDispatch } from "store/store";

interface TelegramGuideModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const TelegramGuideModal = ({ open, setOpen }: TelegramGuideModalProps) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: "Step 1",
      description: (
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Grid2
              container
              sx={{
                background: "var(--neutral-50)",
                p: 2,
                borderRadius: "8px",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Grid2 container alignItems="center" gap={1}>
                <InfoItalicIcon color="var(--neutral-500)" />
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="var(--neutral-700)"
                >
                  Before beginning this process, you need to create the Telegram
                  bot.
                </Typography>
              </Grid2>
              <Box
                display="flex"
                alignItems="center"
                pl={3}
                gap={0.5}
                sx={{
                  cursor: "pointer",
                  color: "var(--primary-main)",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => {
                  navigate(`/app/user-profile/settings`);
                  dispatch(
                    SETTING_TAB({
                      name: "Telegram",
                      type: "TELEGRAM",
                    }),
                  );
                }}
              >
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  color="var(--primary-900)"
                >
                  Steps to Create Your Own Bot
                </Typography>
                <RightIcon color="var(--neutral-500)" />
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 size={12} mt={3}>
            <Typography
              fontSize={14}
              fontWeight={500}
              color="var(--grey-900)"
              lineHeight={"150%"}
            >
              1.{""} Create a Telegram{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                group or channel
              </span>
              , or use an existing one.
            </Typography>
          </Grid2>
          <Grid2 size={12} mt={0.5}>
            <Typography
              fontSize={14}
              fontWeight={500}
              color="var(--grey-900)"
              textAlign={"center"}
            >
              1a. In that Telegram group,
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                {" "}
                add your bot
              </span>{" "}
              and make it an{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                admin.
              </span>
            </Typography>
          </Grid2>
          <Grid2 size={12} ml={5}>
            <Typography
              fontSize={14}
              fontWeight={500}
              color="var(--grey-900)"
              textAlign={"center"}
            >
              1b. The bot will send you the{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                Group Name
              </span>{" "}
              and a{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                Chat ID
              </span>{" "}
              (a 13-digit number). You can see it{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                inside your telegram bot chat
              </span>
              .
            </Typography>
          </Grid2>
          <Grid2 size={12} mt={1}>
            <img
              src={`/TelegramStepsImg/${"step-0.1.png"}`}
              alt=""
              draggable={false}
              style={{
                width: "100%",
                borderRadius: "8px",
                border: "1px solid var(--neutral-100)",
              }}
            />
          </Grid2>
        </Grid2>
      ),
    },
    {
      title: "Step 2",
      description: (
        <Grid2 container spacing={2}>
          <Grid2 size={12} mt={2}>
            <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
              2. In GridKey, Go to:{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                Basket → Profile → Edit Basket → Telegram tab,
              </span>
              and click on the{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                Add New
              </span>{" "}
              button and enter that{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                Group Name & Chat ID
              </span>
              .
            </Typography>
          </Grid2>
          <Grid2 size={12} mt={0.5}>
            <img
              src={`/TelegramStepsImg/${"step-0.2.png"}`}
              alt=""
              draggable={false}
              style={{
                width: "100%",
                borderRadius: "8px",
                border: "1px solid var(--neutral-100)",
              }}
            />
          </Grid2>
          <Grid2 size={12} mt={0.5}>
            <img
              src={`/TelegramStepsImg/${"step-0.3.png"}`}
              alt=""
              draggable={false}
              style={{
                width: "100%",
                borderRadius: "8px",
                border: "1px solid var(--neutral-100)",
              }}
            />
          </Grid2>
          <Grid2 size={12} mt={0.5}>
            <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
              • Click{" "}
              <span style={{ fontWeight: "700", color: "var(--grey-900)" }}>
                Add New
              </span>{" "}
              to complete the integration!
            </Typography>
          </Grid2>
          <Grid2 size={12} mt={0.5}>
            <Typography fontSize={14} fontWeight={500} color="var(--grey-900)">
              Great! Your Telegram is now perfectly connected with GridKey.
            </Typography>
          </Grid2>
        </Grid2>
      ),
    },
  ];

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      width={"849px"}
      minHeight={"470px"}
      modalTitle="Integrate Telegram with GridKey"
      subTitle={
        <span style={{ color: "var(--neutral-500)" }}>
          Follow these simple steps to integrate Telegram in minutes.
        </span>
      }
    >
      <Grid2 container spacing={2} sx={{ height: "100%" }}>
        <Grid2 size={4} sx={{ height: "100%", overflowY: "hidden" }}>
          <List
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {steps.map((step, index) => (
              <ListItem
                key={index}
                onClick={() => setActiveStep(index)}
                sx={{
                  padding: "8px",
                  backgroundColor:
                    activeStep === index ? `var(--primary-50)` : "",
                  color: activeStep === index ? `var(--primary-900)` : "",
                  borderRadius: "8px",
                  ":hover": {
                    cursor: "pointer",
                  },
                }}
              >
                <Typography>{step.title}</Typography>
              </ListItem>
            ))}
          </List>
        </Grid2>
        <Grid2 size={8} sx={{ height: "100%", overflowY: "auto", pr: 1 }}>
          <Typography fontSize={14} fontWeight={500} color="var(--neutral-700)">
            {steps[activeStep].description}
          </Typography>
        </Grid2>
      </Grid2>
    </GKModal>
  );
};

export default TelegramGuideModal;
